import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Skeleton,
  SelectChangeEvent
} from '@mui/material';
import { CompareArrows, ArrowLeft, ArrowRight } from '@mui/icons-material';
import { DiffEditor } from '@monaco-editor/react';
import type { LogisticsObjectEntity } from '@/types/logisticsObject';
import { useLogisticsObject } from '@/hooks/useLogisticsObject';
import { useTheme } from '@/hooks';

interface LogisticsObjectCompareProps {
  logisticsObjectType: LogisticsObjectEntity;
  objectId: string;
}

const LogisticsObjectCompare: React.FC<LogisticsObjectCompareProps> = ({ 
  logisticsObjectType, 
  objectId 
}) => {
  const { mode } = useTheme();
  const {
    metadata,
    changeRequestsLoading,
    compareRevisions
  } = useLogisticsObject(logisticsObjectType, objectId);

  const [revision1, setRevision1] = useState<number>(1);
  const [revision2, setRevision2] = useState<number>(1);
  const [diffMode, setDiffMode] = useState<'inline' | 'side-by-side'>('side-by-side');
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState<{ original: unknown; modified: unknown } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasAutoCompared, setHasAutoCompared] = useState(false);

  // Define handleCompare - accepts optional revision parameters to use specific values
  const handleCompare = async (rev1?: number, rev2?: number) => {
    const compareRev1 = rev1 ?? revision1;
    const compareRev2 = rev2 ?? revision2;
    
    if (compareRev1 === compareRev2) {
      setError('Please select two different revisions to compare');
      return;
    }

    setComparing(true);
    setError(null);
    setComparisonData(null);

    try {
      const result = await compareRevisions(compareRev1, compareRev2);
      if (result) {
        setComparisonData(result);
      } else {
        setError('Failed to fetch comparison data');
      }
    } catch {
      setError('An error occurred while comparing revisions');
    } finally {
      setComparing(false);
    }
  };

  // Get available revisions from metadata and change requests
  const availableRevisions = React.useMemo(() => {
    const revisions = new Set<number>();
    
    // Add current revision from metadata (this should be the latest)
    if (metadata?.revision) {
      revisions.add(metadata.revision);
    }
    
    // Add all revisions from 1 to current revision
    // This ensures we have all actual object revisions, not just change request targets
    if (metadata?.revision) {
      for (let i = 1; i <= metadata.revision; i++) {
        revisions.add(i);
      }
    }

    return Array.from(revisions).sort((a, b) => a - b);
  }, [metadata]);

  // Update initial revisions when available revisions change
  useEffect(() => {
    if (availableRevisions.length >= 2) {
      // Default to second-to-last vs latest revision for comparison
      const latest = availableRevisions[availableRevisions.length - 1];
      const previousToLatest = availableRevisions[availableRevisions.length - 2];
      setRevision1(previousToLatest);
      setRevision2(latest);
    } else if (availableRevisions.length === 1) {
      // Only one revision available - can't compare different versions
      const onlyRevision = availableRevisions[0];
      setRevision1(onlyRevision);
      setRevision2(onlyRevision);
    }
  }, [availableRevisions]);

  // Auto-compare when revisions are set for the first time
  useEffect(() => {
    if (availableRevisions.length >= 2 && revision1 !== revision2 && !hasAutoCompared && !comparing) {
      setHasAutoCompared(true);
      handleCompare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision1, revision2, availableRevisions.length, hasAutoCompared, comparing]);

  const handleRevision1Change = (event: SelectChangeEvent<number>) => {
    const newRevision = Number(event.target.value);
    setRevision1(newRevision);
    // Auto-compare after user changes revision with the new value
    if (hasAutoCompared && newRevision !== revision2) {
      setTimeout(() => handleCompare(newRevision, revision2), 0);
    }
  };

  const handleRevision2Change = (event: SelectChangeEvent<number>) => {
    const newRevision = Number(event.target.value);
    setRevision2(newRevision);
    // Auto-compare after user changes revision with the new value
    if (hasAutoCompared && newRevision !== revision1) {
      setTimeout(() => handleCompare(revision1, newRevision), 0);
    }
  };

  const handleDiffModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'inline' | 'side-by-side') => {
    if (newMode !== null) {
      setDiffMode(newMode);
    }
  };

  const handleLeftArrow = () => {
    // Move comparison window left: decrease both revisions by 1 if possible
    const minRevision = 1;
    if (revision1 > minRevision && revision2 > minRevision) {
      const newRev1 = revision1 - 1;
      const newRev2 = revision2 - 1;
      setRevision1(newRev1);
      setRevision2(newRev2);
      // Auto-compare with the new revision values
      setTimeout(() => handleCompare(newRev1, newRev2), 0);
    }
  };

  const handleRightArrow = () => {
    // Move comparison window right: increase both revisions by 1 if possible
    const maxRevision = availableRevisions.length > 0 ? Math.max(...availableRevisions) : 1;
    if (revision1 < maxRevision && revision2 < maxRevision) {
      const newRev1 = revision1 + 1;
      const newRev2 = revision2 + 1;
      setRevision1(newRev1);
      setRevision2(newRev2);
      // Auto-compare with the new revision values
      setTimeout(() => handleCompare(newRev1, newRev2), 0);
    }
  };

  const renderComparisonEditor = () => {
    if (!comparisonData) {
      return (
        <Box sx={{ 
          height: 500, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'action.hover'
        }}>
          <Typography color="textSecondary">
            Select revisions and click Compare to view differences
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          height: 500,
          '& .monaco-editor': {
            borderRadius: 1,
          },
        }}
      >
        <DiffEditor
          height="500px"
          language="json"
          theme={mode === 'dark' ? 'vs-dark' : 'vs-light'}
          original={JSON.stringify(comparisonData.original, null, 2)}
          modified={JSON.stringify(comparisonData.modified, null, 2)}
          keepCurrentModifiedModel={true} // FIXME : workaround https://github.com/suren-atoyan/monaco-react/issues/647
          keepCurrentOriginalModel={true} // FIXME : workaround https://github.com/suren-atoyan/monaco-react/issues/647
          options={{
            readOnly: true,
            fontSize: 12,
            wordWrap: 'on',
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            renderSideBySide: diffMode === 'side-by-side',
            diffAlgorithm: 'advanced',
            hideUnchangedRegions: {
              enabled: true
            }
          }}
        />
      </Box>
    );
  };

  if (changeRequestsLoading && availableRevisions.length === 0) {
    return (
      <Box>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </Box>
    );
  }

  if (availableRevisions.length < 2) {
    return (
      <Box>
        <Alert severity="info">
          Not enough revisions available for comparison. At least 2 revisions are needed.
          {availableRevisions.length === 1 && ` Current revision: ${availableRevisions[0]}`}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Revision Selection Controls */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          {/* Original Revision Selector - Dynamic width */}
          <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
            <InputLabel>Original Revision</InputLabel>
            <Select
              value={revision1}
              label="Original Revision"
              onChange={handleRevision1Change}
            >
              {availableRevisions.map(rev => (
                <MenuItem key={rev} value={rev}>
                  Revision {rev}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Navigation Buttons - Fixed size */}
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Button
              onClick={handleLeftArrow}
              variant="outlined"
              disabled={revision1 <= 1 || revision2 <= 1}
              sx={{ 
                minWidth: '40px',
                width: '40px',
                height: '40px',
                p: 0
              }}
            >
              <ArrowLeft />
            </Button>
            <Button
              onClick={handleRightArrow}
              variant="outlined"
              disabled={
                availableRevisions.length === 0 || 
                revision1 >= Math.max(...availableRevisions) || 
                revision2 >= Math.max(...availableRevisions)
              }
              sx={{ 
                minWidth: '40px',
                width: '40px',
                height: '40px',
                p: 0
              }}
            >
              <ArrowRight />
            </Button>
          </Box>

          {/* Modified Revision Selector - Dynamic width */}
          <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
            <InputLabel>Modified Revision</InputLabel>
            <Select
              value={revision2}
              label="Modified Revision"
              onChange={handleRevision2Change}
            >
              {availableRevisions.map(rev => (
                <MenuItem key={rev} value={rev}>
                  Revision {rev}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Side by Side / Inline Toggle - Fixed size */}
          <ToggleButtonGroup
            value={diffMode}
            exclusive
            onChange={handleDiffModeChange}
            aria-label="diff mode"
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value="side-by-side" aria-label="side by side">
              Side by Side
            </ToggleButton>
            <ToggleButton value="inline" aria-label="inline">
              Inline
            </ToggleButton>
          </ToggleButtonGroup>
          
          {/* Compare Button - Fixed size, aligned to right */}
          <Button
            variant="contained"
            onClick={() => handleCompare()}
            disabled={comparing || revision1 === revision2}
            startIcon={<CompareArrows />}
            sx={{ 
              height: '40px', 
              minWidth: '120px',
              flexShrink: 0
            }}
          >
            {comparing ? 'Comparing...' : 'Compare'}
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Comparison Display */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {renderComparisonEditor()}
      </Box>

      {/* Legend */}
      {comparisonData && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Green: Added • Red: Removed • Blue: Modified
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LogisticsObjectCompare;